import {
  Add as AddIcon,
  Business as BusinessIcon,
  Event as EventIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

import { useGetNextActionQuery } from "../../store/services/nextActionService";
import {
  getWorkItemIcon,
  getWorkItemTypeName,
} from "../../utils/workItemUtils";
import ActivityLink from "../activity-link/ActivityLink";
import { formatDate } from "./helpers";
import { NextActionCardProps } from "./types";
import VisualActionDialog from "./VisualActionDialog";

const NextActionCard: React.FC<NextActionCardProps> = ({
  customerNo,
  onNextActionChange,
}) => {
  // Dialog status
  const [visualDialogOpen, setVisualDialogOpen] = useState(false);

  // RTK Query hooks
  const {
    data: nextAction,
    isLoading,
    error,
  } = useGetNextActionQuery(customerNo);

  // Hantera öppning av dialog beroende på om det finns en action eller inte
  const handleOpenActionDialog = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setVisualDialogOpen(true);
  };

  // Laddar eller felläge
  if (isLoading) {
    return (
      <Card>
        <CardContent sx={{ py: 1, px: 2 }}>
          <Box display="flex" alignItems="center" height="40px">
            <CircularProgress size={24} />
            <Typography variant="body2" ml={1}>
              Laddar nästa action...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ borderLeft: 4, borderColor: "error.main" }}>
        <CardContent sx={{ py: 1, px: 2 }}>
          <Box display="flex" alignItems="center" height="40px">
            <Typography variant="body2" color="error">
              Kunde inte hämta nästa aktivitet
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Om det inte finns någon nästa action
  if (!nextAction?.HasNextAction) {
    return (
      <Card
        sx={{
          position: "relative",
          borderRadius: "8px",
          p: "2px", // Border width
          background:
            "linear-gradient(60deg, #d32f2f, #f44336, #ef5350, #e57373, #d32f2f)",
          backgroundSize: "300% 300%",
          animation: "animatedGradient 3s ease infinite",

          "@keyframes animatedGradient": {
            "0%": {
              backgroundPosition: "0% 50%",
            },
            "50%": {
              backgroundPosition: "100% 50%",
            },
            "100%": {
              backgroundPosition: "0% 50%",
            },
          },
        }}
      >
        <CardContent
          sx={{
            p: 2,
            borderRadius: "6px",
            backgroundColor: "white",
            height: "100%",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="body2" color="text.secondary">
              Ingen schemalagd aktivitet
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenActionDialog}
              size="small"
            >
              Aktivitet
            </Button>
          </Box>

          {/* Visuell dialog för att skapa ny action */}
          <VisualActionDialog
            open={visualDialogOpen}
            customerNo={customerNo}
            onClose={() => setVisualDialogOpen(false)}
            onNextActionChange={onNextActionChange}
          />
        </CardContent>
      </Card>
    );
  }
  console.log("nextAction", nextAction);
  // Om det finns en befintlig nästa action
  const cardContent = (
    <CardContent sx={{ py: 1.5, px: 2 }}>
      <Grid container spacing={1}>
        {/* Titel och typ - rad 1 */}
        <Grid item xs={12}>
          <Box display="flex" alignItems="center">
            {getWorkItemIcon(nextAction.WorkItemType ?? null)}
            <Box mr={1} sx={{ flex: 1, overflow: "hidden" }}>
              <Typography variant="subtitle1" noWrap>
                {nextAction.WorkItemName || "Aktivitet"}
              </Typography>
            </Box>
            {nextAction.WorkItemType !== undefined && (
              <Chip
                label={getWorkItemTypeName(nextAction.WorkItemType) || "Action"}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: 24, ml: 1 }}
              />
            )}
          </Box>
        </Grid>

        {/* Beskrivning - rad 2 (om det finns) */}
        {nextAction.WorkItemDescription && (
          <Grid item xs={12}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                overflow: "hidden",
                textOverflow: "ellipsis",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
              }}
            >
              {nextAction.WorkItemDescription}
            </Typography>
          </Grid>
        )}

        {/* Metadata - rad 3 */}
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <EventIcon fontSize="small" sx={{ mr: 0.5 }} />
                {formatDate(nextAction.ScheduledNextActionDate)}
              </Typography>

              {nextAction.AssignedToName && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <PersonIcon fontSize="small" sx={{ mr: 0.5 }} />
                  {nextAction.AssignedToName}
                </Typography>
              )}

              {nextAction.TeamName && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <BusinessIcon fontSize="small" sx={{ mr: 0.5 }} />
                  {nextAction.TeamName}
                </Typography>
              )}
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </CardContent>
  );

  return (
    <>
      <Card
        sx={{
          mb: 2,
          cursor: "pointer",
          transition: "all 0.2s",
          "&:hover": { boxShadow: 3 },
        }}
      >
        {nextAction.WorkItemNo ? (
          <ActivityLink
            to={`/activity/${nextAction.WorkItemNo}`}
            customerNo={customerNo}
            workItemNo={nextAction.WorkItemNo}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {cardContent}
          </ActivityLink>
        ) : (
          cardContent
        )}
      </Card>
    </>
  );
};

export default NextActionCard;
